/**
 * Forgot Password Page Component - Phoenix Premium Design
 *
 * Password reset form with email verification.
 * Brand color: #00468C (Phoenix blue)
 *
 * Based on: docs/design/UI_IMPROVEMENTS_PLAN.md SKETCH 3
 */

import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Link } from 'react-router-dom';

// Icons
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-10 h-10">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Zadejte platnou emailovou adresu');
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('Účet s tímto emailem neexistuje');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Příliš mnoho pokusů. Zkuste to později.');
      } else {
        setError(err.message || 'Chyba při odesílání emailu');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8 relative overflow-x-hidden"
      style={{ background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)' }}
    >
      {/* Decorative circles */}
      <div className="absolute w-[600px] h-[600px] top-[-200px] right-[-100px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
      <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-50px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

      <div
        className="w-full max-w-md p-12 rounded-3xl relative z-10"
        style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
      >
        {sent ? (
          /* Success State */
          <div className="text-center">
            {/* Success Icon */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'var(--success-100)' }}
            >
              <MailIcon />
            </div>

            <h1 className="font-display text-2xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Email odeslán!
            </h1>

            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              Odkaz pro obnovení hesla byl odeslán na{' '}
              <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>
            </p>

            <div
              className="p-4 rounded-xl mb-4"
              style={{ background: 'var(--bg-tertiary)' }}
            >
              <div className="flex items-start gap-3">
                <span style={{ color: 'var(--phoenix-500)' }}>
                  <CheckCircleIcon />
                </span>
                <p className="text-sm text-left" style={{ color: 'var(--text-secondary)' }}>
                  Odkaz je platný 1 hodinu.
                </p>
              </div>
            </div>

            <div
              className="p-4 rounded-xl mb-6"
              style={{ background: 'var(--warning-50, #fffbeb)', border: '1px solid var(--warning-200, #fde68a)' }}
            >
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--warning-800, #92400e)' }}>
                Nevidíte email? Zkontrolujte složku Spam / Hromadné
              </p>
              <p className="text-xs" style={{ color: 'var(--warning-700, #b45309)' }}>
                Email přijde od <strong>noreply@phoenix-analytics-44917.firebaseapp.com</strong>.
                Některé poštovní služby ho mohou zařadit do spamu.
              </p>
            </div>

            <Link
              to="/login"
              className="btn-phoenix btn-phoenix-primary w-full"
            >
              Zpět na přihlášení
            </Link>

            <button
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
              className="mt-4 text-sm font-medium transition-colors w-full py-2"
              style={{ color: 'var(--phoenix-500)' }}
            >
              Nepřišel email? Zkusit znovu
            </button>
          </div>
        ) : (
          /* Form State */
          <>
            {/* Back Link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors mb-8"
              style={{ color: 'var(--phoenix-500)' }}
            >
              <ArrowLeftIcon />
              Zpět na přihlášení
            </Link>

            {/* Header */}
            <div className="mb-8">
              <h1 className="font-display text-[1.75rem] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Obnovení hesla
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                Zadejte svůj email a pošleme vám odkaz pro obnovení hesla.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Emailová adresa
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  className={`input-phoenix ${error ? 'error' : ''}`}
                  placeholder="vas@email.cz"
                  autoComplete="email"
                  autoFocus
                />
                {error && (
                  <div className="flex items-center gap-2 mt-2 text-[0.8125rem]" style={{ color: 'var(--danger-600)' }}>
                    <AlertIcon />
                    {error}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-phoenix btn-phoenix-primary w-full"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Odeslat odkaz'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                Vzpomněli jste si na heslo?{' '}
                <Link
                  to="/login"
                  className="font-medium transition-colors"
                  style={{ color: 'var(--phoenix-500)' }}
                >
                  Přihlásit se
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
